import React from 'react';

import { useTranslate } from '@/core/locales';
import { fNumber } from '@/core/utils/format-number';
import { TRANSLATION_NAMESPACES } from '@/core/constants';
import getCurrencySymbolByCode from '@/core/utils/get-currency-symbol';

import { Iconify } from '@/components/iconify';
import { Tooltip } from '@/components/ui/Tooltip';
import { Box, Button, Typography } from '@/components/ui';

import { paths } from '@/routes/paths';
import { RouterLink } from '@/routes/components';

type PackageCard = {
  package: any;
  currentPackage: any;
  features?: any[];
  FEATURE_CONFIG?: Record<string, { order?: number }>;
};

type FeatureConfigKey = string;

export const PackageCard: React.FC<PackageCard> = ({
  package: pack,
  currentPackage,
  features = pack?.features ?? [],
  FEATURE_CONFIG = {},
}) => {
  const { t } = useTranslate(TRANSLATION_NAMESPACES.COMMON);
  const currencySymbol = getCurrencySymbolByCode(currentPackage?.currencyCode);

  const getFeatureText = (feature: any, limitations?: any[]): string => {
    const limitation = limitations?.find((l) => l.featureId === feature.id);

    const durationUnitMap: Record<string, string> = {
      day: t('features.durations.day'),
      month: t('features.durations.month'),
      year: t('features.durations.year'),
      unlimited: t('features.durations.unlimited'),
    };

    const translatedDurationUnit = limitation?.durationUnit
      ? durationUnitMap[limitation.durationUnit]
      : undefined;

    return t(`features.${feature.slug}`, {
      count: limitation?.value ?? 0,
      durationUnit: translatedDurationUnit,
    });
  };

  const colorMap = {
    beginner: {
      text: 'text-[var(--color-warning)]',
      button: 'bg-[var(--color-warning)] hover:bg-[var(--color-warning-dark)]',
      badge: 'bg-[var(--color-warning)]/5 border border-[var(--color-warning)]',
    },
    experienced: {
      text: 'text-[var(--color-primary-dark)]',
      button: 'bg-[var(--color-primary-dark)] hover:bg-[var(--color-primary-darker)]',
      badge: 'bg-[var(--color-primary)]/5 border border-[var(--color-primary)]',
    },
    professional: {
      text: 'text-[var(--color-secondary)]',
      button: 'bg-[var(--color-secondary)] hover:bg-[var(--color-secondary-dark)]',
      badge: 'bg-[var(--color-secondary)]/5 border border-[var(--color-secondary)]',
    },
  };

  const currentColor = colorMap[pack.slug as keyof typeof colorMap];

  const sortedFeatures = [...features].sort((first, second) => {
    const firstOrder = FEATURE_CONFIG[first.slug as FeatureConfigKey]?.order ?? 99;
    const secondOrder = FEATURE_CONFIG[second.slug as FeatureConfigKey]?.order ?? 99;
    return firstOrder - secondOrder;
  });

  return (
    <Box className="p-3 rounded-3xl border border-[var(--color-black)]/10 bg-[var(--color-grey-100)] shadow-sm hover:shadow-md transition duration-300 flex flex-col h-full">
      {/*<Box className="bg-[var(--color-white)] rounded-2xl p-5 border border-[var(--color-black)]/10 flex flex-col sm:min-h-[640px] sm:max-h-[650px] md:min-h-[440px] md:max-h-[450px] min-h-[340px] max-h-[350px]">*/}
      <Box className="bg-[var(--color-white)] rounded-2xl p-5 border border-[var(--color-black)]/10 flex flex-col h-[340px] xs:h-[400px] sm:h-[640px] md:h-[440px]">
        <Box className="flex justify-between items-start">
          <Typography className={`text-lg font-semibold ${currentColor?.text}`}>
            {pack.name}
          </Typography>

          {currentPackage?.discount > 0 && (
            <Box className="bg-[var(--color-secondary)] text-[var(--color-white)] shadow-md font-bold px-3 py-1 rounded-lg text-xs sm:text-sm md:text-base">
              {t('bundles.packageDiscount', { discount: currentPackage.discount })}
            </Box>
          )}
        </Box>

        <Box>
          <Typography className="text-md font-medium">
            {t('bundles.total')}
            {fNumber(
              currentPackage.discountPrice ? currentPackage.discountPrice : currentPackage.price
            )}{' '}
            {currencySymbol}
          </Typography>

          <Typography variant="h2" className="my-1">
            {fNumber(currentPackage?.minimalPeriodPrice)} {currencySymbol}/ {t('bundles.mon')}
          </Typography>

          {(currentPackage.discountPrice || currentPackage.periodicDiscount) && (
            <Typography variant="h5" className="text-[var(--color-grey-400)] line-through mt-0.5">
              {fNumber(currentPackage?.originalMinimalPeriodPrice)} {currencySymbol}/{' '}
              {t('bundles.mon')}
            </Typography>
          )}
        </Box>

        <Typography className="text-sm mb-4 mt-2">{pack.description}</Typography>

        <Box className="mt-auto">
          <Button
            fullWidth
            component={RouterLink}
            href={paths.auth.singIn}
            className={`rounded-lg py-1 font-semibold text-[var(--color-white)] ${currentColor?.button}`}
          >
            {t('bundles.payBtn')}
          </Button>
        </Box>
      </Box>

      <Box className="p-4 mt-2 space-y-1">
        {sortedFeatures.map((feature: any) => {
          const featureText = getFeatureText(feature, currentPackage.limitations);

          const renderFeatureText =
            feature.slug === 'auto-discount'
              ? featureText.split('<----->').map((part, index) => (
                  <Typography variant="body2" key={index} className="mb-2">
                    {part.trim()}
                  </Typography>
                ))
              : featureText;

          const tooltipContent = (
            <Box>
              {feature.description && (
                <Box className="mb-1">
                  <Typography variant="caption" className="font-extrabold block">
                    {t('subscriptions.description')}
                  </Typography>
                  <Typography variant="body2">{feature.description}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" className="font-extrabold block">
                  {t('subscriptions.limit')}
                </Typography>
                <Typography variant="body2">{renderFeatureText}</Typography>
              </Box>
            </Box>
          );

          return (
            <Box key={feature.name} className="flex items-center justify-between gap-1 mb-1">
              <Box className="flex items-center gap-1">
                <Iconify icon="solar:check-circle-bold" className={currentColor?.text} width={18} />
                <Typography variant="body2" color="text.primary">
                  {feature.name}
                </Typography>
              </Box>

              <Tooltip title={tooltipContent}>
                <Box className="flex-shrink-0 cursor-pointer">
                  <Iconify icon="eva:info-outline" width={16} />
                </Box>
              </Tooltip>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
